let activeMegaSearchOwner = null;

const vehicleSearchContext = (() => {
  const preferenceKey = 'maxtondesign_vehicle_filter_enabled';
  const listeners = new Set();
  let storage = null;
  let savedVehicles = [];
  let selectedCar = null;
  let filterEnabled = false;
  let defaultCarText = '';
  let readyPromise = null;
  let eventsBound = false;
  let activeInstance = null;
  let lastQuery = '';

  const supportsLocalStorage = () => {
    try {
      return typeof window !== 'undefined' && 'localStorage' in window && window.localStorage !== null;
    } catch (error) {
      return false;
    }
  };

  const getStoredPreference = () => {
    if (!supportsLocalStorage()) return null;
    const value = window.localStorage.getItem(preferenceKey);
    if (value === null) return null;
    return value === 'true';
  };

  const setStoredPreference = (value) => {
    if (!supportsLocalStorage()) return;
    if (value === null) {
      window.localStorage.removeItem(preferenceKey);
      return;
    }
    window.localStorage.setItem(preferenceKey, value ? 'true' : 'false');
  };

  const ensureStorage = () => {
    if (!storage && typeof VehicleStorage !== 'undefined') {
      storage = new VehicleStorage();
    }
  };

  const extractDefaultCarText = () => {
    if (defaultCarText) return defaultCarText;
    const translationsElement = document.getElementById('car-drawer-translations');
    if (!translationsElement) return defaultCarText;

    try {
      const parsed = JSON.parse(translationsElement.textContent);
      if (parsed?.defaultCarText) {
        defaultCarText = parsed.defaultCarText;
      }
    } catch (error) {
      // Swallow JSON issues, we'll fallback to static markup text.
    }

    return defaultCarText;
  };

  const applyPreference = () => {
    const storedPreference = getStoredPreference();

    if (storedPreference === null) {
      filterEnabled = false;
    } else {
      filterEnabled = storedPreference && Boolean(selectedCar);
    }

    if (!selectedCar) {
      filterEnabled = false;
    }
  };

  const getSelectedVehicleData = () => {
    if (!selectedCar || !Array.isArray(savedVehicles)) return null;
    const vehicleEntry = savedVehicles.find((vehicle) => vehicle?.name === selectedCar);
    return vehicleEntry?.data || null;
  };

  const snapshot = () => ({
    selectedCar,
    selectedCarData: getSelectedVehicleData(),
    filterEnabled,
    defaultCarText,
  });

  const notify = () => {
    const state = snapshot();
    listeners.forEach((listener) => listener(state));
  };

  const registerActiveInstance = (instance, query) => {
    activeInstance = instance;
    lastQuery = query;
  };

  const requestRefresh = () => {
    if (activeInstance && lastQuery && typeof activeInstance.refreshSearchResults === 'function') {
      activeInstance.refreshSearchResults(lastQuery);
    }
  };

  const refreshSelectedFromStorage = async () => {
    ensureStorage();
    savedVehicles = storage ? await storage.loadVehicles() : [];
    selectedCar = storage ? await storage.loadSelected() : null;
    applyPreference();
    notify();
  };

  const bindGlobalEvents = () => {
    if (eventsBound) return;

    document.addEventListener('car-selected', async (event) => {
      ensureStorage();
      savedVehicles = storage ? await storage.loadVehicles() : savedVehicles;
      if (event?.detail?.carName) {
        selectedCar = event.detail.carName;
      } else {
        selectedCar = storage ? await storage.loadSelected() : null;
      }
      applyPreference();
      notify();
      requestRefresh();
    });

    document.addEventListener('vehicles-deleted', async () => {
      await refreshSelectedFromStorage();
      requestRefresh();
    });

    document.addEventListener('car-added', async () => {
      await refreshSelectedFromStorage();
      notify();
    });

    eventsBound = true;
  };

  const init = async () => {
    if (!readyPromise) {
      readyPromise = (async () => {
        ensureStorage();
        extractDefaultCarText();
        savedVehicles = storage ? await storage.loadVehicles() : [];
        selectedCar = storage ? await storage.loadSelected() : null;
        applyPreference();
        bindGlobalEvents();
        return snapshot();
      })();
    }
    return readyPromise;
  };

  return {
    async init() {
      await init();
      return snapshot();
    },
    subscribe(listener) {
      listeners.add(listener);
      if (readyPromise) {
        readyPromise.then(() => listener(snapshot()));
      }
      return () => listeners.delete(listener);
    },
    setFilterPreference(enabled, sourceInstance) {
      if (!selectedCar) {
        setStoredPreference(false);
      } else {
        setStoredPreference(Boolean(enabled));
      }
      applyPreference();
      notify();
      if (sourceInstance) {
        registerActiveInstance(sourceInstance, sourceInstance?.searchTerm || lastQuery || '');
      }
      requestRefresh();
    },
    registerActiveInstance,
    getSelectedCar() {
      return selectedCar;
    },
    isFilterEnabled() {
      return filterEnabled && Boolean(selectedCar);
    },
    getDefaultCarText() {
      return defaultCarText;
    },
    async refreshFromStorage() {
      await refreshSelectedFromStorage();
    },
  };
})();

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults =
      this.querySelector('[data-predictive-search]') ||
      document.querySelector('[data-header-search-dropdown] [data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.searchLabelTemplate = this.dataset?.searchLabelTemplate;
    this.searchDropdown = document.querySelector('[data-header-search-dropdown]');
    this.searchOverlay = document.querySelector('[data-header-search-overlay]');
    this.flyoutJustOpened = false;
    this.pointerDownInsideFlyout = false;
    this.onPointerDownHandler = this.onPointerDown.bind(this);
    this.onDocumentClickHandler = this.onDocumentClick.bind(this);
    document.addEventListener('pointerdown', this.onPointerDownHandler);

    this.vehicleInput = this.querySelector('[data-search-vehicle-input]');
    this.selectedVehicleName = null;
    this.selectedVehicleData = null;
    this.vehicleFilterEnabled = false;
    this.vehicleMatchers = null;
    this.preventCloseOnBlur = false;
    this.megaToggle = null;
    this.megaCarName = null;
    this.megaCarNameDefault = '';
    this.vehicleStateUnsubscribe = null;
    this.handleVehicleStateUpdate = this.handleVehicleStateUpdate.bind(this);
    this.handleVehicleToggle = this.handleVehicleToggle.bind(this);

    vehicleSearchContext.init().then(() => {
      this.vehicleStateUnsubscribe = vehicleSearchContext.subscribe(this.handleVehicleStateUpdate);
    });

    this.initVehicleElements();

    this.cacheMegaElements();
    this.storeDefaultMegaState();
    this.ctaClickHandler = null;
    this.maxMegaResults = 4;
    this.lastNonEmptyMegaMarkup = '';

    this.setupEventListeners();
  }

  initVehicleElements() {
    if (this.classList.contains('mega-search__form')) {
      this.megaToggle = document.querySelector('[data-search-vehicle-toggle]');
      this.megaCarName = document.querySelector('[data-search-car-name]');
      if (this.megaCarName) {
        this.megaCarNameDefault = this.megaCarName.textContent.trim();
      }
      if (this.megaToggle && !this.megaToggle.dataset.vehicleToggleBound) {
        this.megaToggle.addEventListener('change', this.handleVehicleToggle);
        this.megaToggle.dataset.vehicleToggleBound = 'true';
      }
    }
  }

  handleVehicleStateUpdate(state) {
    this.selectedVehicleName = state?.selectedCar || null;
    this.selectedVehicleData = state?.selectedCarData || null;
    this.vehicleFilterEnabled = Boolean(state?.filterEnabled) && Boolean(this.selectedVehicleName);
    this.vehicleMatchers = this.buildVehicleMatchers(this.selectedVehicleData);

    if (this.vehicleInput) {
      const value = this.vehicleFilterEnabled ? this.selectedVehicleName : '';
      this.vehicleInput.value = value;
      this.vehicleInput.defaultValue = value;
    }

    if (this.megaToggle) {
      this.megaToggle.checked = this.vehicleFilterEnabled;
      this.megaToggle.disabled = !this.selectedVehicleName;
    }

    if (this.megaCarName) {
      const label = this.selectedVehicleName || state?.defaultCarText || this.megaCarNameDefault;
      this.megaCarName.textContent = label;
      this.megaCarName.classList.toggle('text-400', !this.selectedVehicleName);
    }
  }

  handleVehicleToggle(event) {
    if (!this.selectedVehicleName) {
      event.target.checked = false;
      return;
    }

    this.preventCloseOnBlur = true;
    this.pointerDownInsideFlyout = true;

    const enabled = Boolean(event.target.checked && this.selectedVehicleName);
    this.vehicleFilterEnabled = enabled;
    this.vehicleMatchers = this.buildVehicleMatchers(this.selectedVehicleData);

    if (this.vehicleInput) {
      const value = enabled ? this.selectedVehicleName : '';
      this.vehicleInput.value = value;
      this.vehicleInput.defaultValue = value;
    }

    vehicleSearchContext.setFilterPreference(event.target.checked, this);

    const releasePointerGuard = () => {
      this.pointerDownInsideFlyout = false;
      this.preventCloseOnBlur = false;
    };

    requestAnimationFrame(() => {
      if (this.input && document.activeElement !== this.input) {
        this.input.focus({ preventScroll: true });
      }

      const query = typeof this.getQuery === 'function' ? this.getQuery() : (this.input?.value || '').trim();

      if (query) {
        this.refreshSearchResults(query);
      }

      requestAnimationFrame(releasePointerGuard);
    });
  }

  shouldFilterByVehicle() {
    return Boolean(this.vehicleFilterEnabled && this.selectedVehicleName);
  }

  normalizeToTokens(value, { joinVariants = true } = {}) {
    if (!value) return [];
    const normalized = value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    const splitTokens = normalized.split(/[^a-z0-9]+/).filter(Boolean);
    if (!joinVariants || splitTokens.length <= 1) return splitTokens;

    const joined = splitTokens.join('');
    return joined ? Array.from(new Set([...splitTokens, joined])) : splitTokens;
  }

  buildVehicleMatchers(carData) {
    if (!carData) return null;

    const toTokenSet = (...values) => {
      const tokens = [];
      values.forEach((value) => {
        tokens.push(...this.normalizeToTokens(value));
      });
      return Array.from(new Set(tokens));
    };

    const brandTokens = toTokenSet(carData.brand);

    const coreTokens = toTokenSet(carData.model, carData.version, carData.generation);

    if (!coreTokens.length && carData.bodytype) {
      coreTokens.push(...this.normalizeToTokens(carData.bodytype));
    }

    const modelTokens = this.getModelTokens(carData);

    return {
      brandTokens,
      coreTokens,
      modelTokens,
    };
  }

  getModelTokens(carData) {
    if (!carData?.model) return [];

    const genericTerms = new Set([
      'sline',
      's-line',
      'mline',
      'm-line',
      'rline',
      'r-line',
      'sport',
      'sportline',
      'line',
      'edition',
      'facelift',
      'fl',
      'sedan',
      'coupe',
      'avant',
      'touring',
      'wagon',
      'hatchback',
      'comfort',
      'luxury',
      'business',
      'premium',
      'competition',
    ]);

    const modelStr = carData.model.toLowerCase();
    const tokens = this.normalizeToTokens(modelStr, { joinVariants: false });

    let modelTokens = tokens.filter((token) => {
      if (genericTerms.has(token)) return false;
      return token.length >= 2 && token.length <= 6 && /[0-9]/.test(token);
    });

    if (!modelTokens.length) {
      modelTokens = tokens.filter((token) => {
        if (genericTerms.has(token)) return false;
        return token.length >= 3;
      });
    }

    return Array.from(new Set(modelTokens));
  }

  buildVehicleSearchTerm(term) {
    if (!this.shouldFilterByVehicle() || !this.selectedVehicleData) return term;

    const parts = [term];
    const { brand, model, generation } = this.selectedVehicleData;

    const addPart = (value) => {
      if (!value) return;
      const tokens = this.normalizeToTokens(value, { joinVariants: false });
      if (tokens.length) {
        parts.push(tokens.join(' '));
      }
    };

    addPart(brand);
    addPart(model);
    addPart(generation);

    const dedupedTokens = Array.from(new Set(parts.join(' ').split(/\s+/).filter(Boolean)));

    return dedupedTokens.join(' ');
  }

  refreshSearchResults(queryFromContext) {
    const term = (queryFromContext || this.searchTerm || '').trim();
    if (!term.length) return;
    this.getSearchResults(term);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Handle clicks outside to close flyout
    document.addEventListener('click', this.onDocumentClickHandler);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      const shouldRefocus = document.activeElement === this.input;
      this.resetMegaAfterClear({ abort: true });
      if (this.closeFlyoutIfEmptyDefault({ refocus: shouldRefocus })) {
        this.hideOverlay();
      }
      return;
    }

    if (!this.isFlyoutOpen()) {
      this.openFlyout();
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length || this.querySelector('[aria-selected="true"] a')) return void event.preventDefault();

    // TODO: Bring back this function on vehicle search
    if (false) {
      const vehicleTerm = this.buildVehicleSearchTerm(query);
      if (vehicleTerm && vehicleTerm !== query) {
        this.input.value = vehicleTerm;
      }

      const form = this.input?.form;
      if (form) {
        let vehicleInput = form.querySelector('input[name="vehicle"]');
        if (!vehicleInput) {
          vehicleInput = document.createElement('input');
          vehicleInput.type = 'hidden';
          vehicleInput.name = 'vehicle';
          form.appendChild(vehicleInput);
        }
        vehicleInput.value = this.selectedVehicleName || '';
      }
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.resetMegaAfterClear();
    }
  }

  onFocus() {
    const defaultContainer = this.searchDropdown?.querySelector('[data-search-default]');
    const hasDefaultContent = defaultContainer?.dataset?.searchHasDefault === 'true';
    const hasResults = this.getAttribute('results') === 'true';

    const currentSearchTerm = this.getQuery();

    if (!hasDefaultContent && !hasResults && !currentSearchTerm.length) {
      return;
    }

    this.openFlyout();

    if (!currentSearchTerm.length) {
      this.showMegaDefaultView(false);
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.flyoutJustOpened) {
        this.pointerDownInsideFlyout = false;
        return;
      }

      if (this.preventCloseOnBlur) {
        return;
      }

      if (this.pointerDownInsideFlyout) {
        this.pointerDownInsideFlyout = false;
        return;
      }

      const activeElement = document.activeElement;
      const isHeaderSearchFocus = Boolean(activeElement?.closest?.('.header__search, .header__search-field'));

      if (isHeaderSearchFocus) {
        return;
      }

      if (!this.contains(activeElement)) {
        this.close(false, { preserveMega: true });
        // Check if the new focused element is inside the flyout
        const flyoutContainsFocus = this.searchDropdown?.contains(activeElement);
        if (!flyoutContainsFocus) {
          this.closeFlyout();
        }
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    // Handle escape key to close flyout
    if (event.code === 'Escape' && this.isFlyoutOpen()) {
      event.preventDefault();
      this.closeFlyout();
      this.input.blur();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const sanitizedTerm = searchTerm.replace(/\s+/g, '-').toLowerCase();
    const vehicleKey = this.shouldFilterByVehicle() ? `::vehicle:${this.selectedVehicleName}` : '::vehicle:none';
    const queryKey = `${sanitizedTerm}${vehicleKey}`;
    this.setLiveRegionLoadingState();

    vehicleSearchContext.registerActiveInstance(this, searchTerm);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const requestToken = `${queryKey}:${Date.now()}`;
    this.activeRequestToken = requestToken;

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
    });

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        if (this.activeRequestToken === requestToken) {
          this.renderSearchResults(resultsMarkup);
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement ??=
      this.querySelector('.predictive-search-status') ||
      document.querySelector('[data-header-search-dropdown] .predictive-search-status');
    this.loadingText ??= this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.updateMegaSearchContent(resultsMarkup);

    this.setLiveRegionResults();
    this.openFlyout();
    this.open();
  }

  cacheMegaElements() {
    if (!this.searchDropdown) {
      this.megaElements = null;
      return null;
    }

    const defaultContainer = this.searchDropdown.querySelector('[data-search-default]');
    const resultsContainer = this.searchDropdown.querySelector('[data-search-results]');
    const defaultGrid = this.searchDropdown.querySelector('[data-search-product-grid]');
    const suggestionsContainer = this.searchDropdown.querySelector('[data-search-default-suggestions]');
    const suggestionsList = suggestionsContainer?.querySelector('[data-search-suggestions-list]');
    const suggestionsTitle = suggestionsContainer?.querySelector('.mega-search__suggestions-title');
    const footer = this.searchDropdown.querySelector('[data-search-default-footer]');

    this.megaElements = {
      defaultContainer,
      resultsContainer,
      defaultGrid,
      suggestionsContainer,
      suggestionsList,
      suggestionsTitle,
      footer,
    };

    return this.megaElements;
  }

  storeDefaultMegaState() {
    if (!this.megaElements) return;

    if (typeof this.defaultProductMarkup === 'undefined' && this.megaElements.defaultGrid) {
      this.defaultProductMarkup = this.megaElements.defaultGrid.innerHTML;
    }

    if (typeof this.defaultSuggestionsMarkup === 'undefined' && this.megaElements.suggestionsList) {
      this.defaultSuggestionsMarkup = this.megaElements.suggestionsList.innerHTML;
    }

    if (typeof this.defaultSuggestionsTitle === 'undefined' && this.megaElements.suggestionsTitle) {
      this.defaultSuggestionsTitle = this.megaElements.suggestionsTitle.textContent;
    }

    if (typeof this.defaultFooterHTML === 'undefined' && this.megaElements.footer) {
      this.defaultFooterHTML = this.megaElements.footer.innerHTML;
    }

    if (
      (typeof this.suggestionIconHTML === 'undefined' || this.suggestionIconHTML === '') &&
      this.megaElements.suggestionsContainer
    ) {
      const icon = this.megaElements.suggestionsContainer.querySelector('.mega-search__chip-icon');
      this.suggestionIconHTML = icon ? icon.innerHTML : '';
    }
  }

  updateMegaSearchContent(resultsMarkup) {
    if (!this.megaElements?.defaultGrid) return;

    const tempWrapper = document.createElement('div');
    tempWrapper.innerHTML = resultsMarkup;

    this.showMegaDefaultView(true);
    activeMegaSearchOwner = this;

    const maxResults = Number(this.maxMegaResults) || 4;
    const rawProductItems = Array.from(
      tempWrapper.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item'),
    );
    const productItems = this.filterProductItems(rawProductItems, maxResults);
    const suggestionItems = Array.from(
      tempWrapper.querySelectorAll('#predictive-search-results-queries-list .predictive-search__list-item'),
    );
    const suggestionHeading = tempWrapper.querySelector('#predictive-search-queries')?.textContent?.trim();
    const searchForButton = tempWrapper.querySelector('#predictive-search-option-search-keywords button');
    const grid = this.megaElements.defaultGrid;
    grid.innerHTML = '';
    const isVehicleFiltering = this.shouldFilterByVehicle();

    if (!productItems.length) {
      if (!isVehicleFiltering && this.lastNonEmptyMegaMarkup) {
        grid.innerHTML = this.lastNonEmptyMegaMarkup;
        this.searchDropdown?.classList.add('header__search-dropdown--has-results');
      } else {
        this.searchDropdown?.classList.remove('header__search-dropdown--has-results');
        const searchTerm = this.input.value.trim();
        const label = searchForButton?.textContent?.trim() || this.buildSearchLabel(searchTerm);
        this.renderMegaNoResultsState(grid, label);
      }
    } else {
      this.searchDropdown?.classList.add('header__search-dropdown--has-results');
      productItems.forEach((item) => {
        const card = this.buildProductCard(item);
        if (card) grid.appendChild(card);
      });
      if (!isVehicleFiltering) {
        this.lastNonEmptyMegaMarkup = grid.innerHTML;
      }
    }

    this.updateSuggestions(suggestionItems, suggestionHeading);
    this.updateCta(searchForButton);
  }

  showMegaDefaultView(force = false) {
    if (!this.megaElements || !this.megaElements.defaultContainer) {
      this.cacheMegaElements();
    }

    const { defaultContainer, resultsContainer } = this.megaElements || {};
    const hasDefaultContent = defaultContainer?.dataset?.searchHasDefault === 'true';

    if (resultsContainer) {
      resultsContainer.setAttribute('hidden', 'true');
    }

    if (defaultContainer) {
      if (force || hasDefaultContent) {
        defaultContainer.removeAttribute('hidden');
        defaultContainer.hidden = false;
      } else {
        defaultContainer.setAttribute('hidden', 'true');
        defaultContainer.hidden = true;
      }
    }
  }

  closeFlyoutIfEmptyDefault({ refocus = false } = {}) {
    const hasDefaultContent =
      this.searchDropdown?.querySelector('[data-search-default]')?.dataset?.searchHasDefault === 'true';
    if (hasDefaultContent) return false;
    const defaultContainer = this.searchDropdown?.querySelector('[data-search-default]');
    if (defaultContainer) {
      defaultContainer.setAttribute('hidden', 'true');
      defaultContainer.hidden = true;
    }
    const header = document.querySelector('.header');
    const mobileSearchActive = header?.classList.contains('header--search-mode');
    if (mobileSearchActive) {
      return false;
    }
    this.pointerDownInsideFlyout = false;
    this.closeFlyout();
    this.searchDropdown?.classList.remove('header__search-dropdown--has-results');
    this.input.setAttribute('aria-expanded', false);
    if (refocus) {
      this.input.focus();
    }
    return true;
  }

  resetMegaAfterClear({ abort = false } = {}) {
    if (abort) {
      this.abortController.abort();
      this.abortController = new AbortController();
    }

    this.searchTerm = '';

    const selected = this.querySelector('[aria-selected=\"true\"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');

    this.removeAttribute('results');
    this.removeAttribute('loading');

    this.predictiveSearchResults?.removeAttribute('style');

    const restored = this.restoreDefaultMegaSearch(true);
    if (restored) {
      this.showMegaDefaultView(false);
    }

    this.pointerDownInsideFlyout = true;
    if (this.closeFlyoutIfEmptyDefault({ refocus: document.activeElement === this.input })) {
      this.hideOverlay();
      return;
    }

    setTimeout(() => {
      this.openFlyout();
      if (!this.isOpen) {
        this.open();
      } else {
        this.input.setAttribute('aria-expanded', true);
      }
      this.input.focus();
    }, 0);
  }

  renderMegaNoResultsState(gridNode, label) {
    const message = document.createElement('p');
    message.classList.add('mega-search__no-results', 'text-body-large', 'text-700');
    message.textContent = label || this.buildSearchLabel(this.input.value.trim());
    gridNode.appendChild(message);
  }

  buildProductCard(productItem) {
    const link = productItem.querySelector('a');
    if (!link) return null;

    const card = document.createElement('article');
    card.classList.add('mega-search__product-card', 'mega-search__product-card--result');

    const linkWrapper = document.createElement('a');
    linkWrapper.classList.add('mega-search__product-link');
    linkWrapper.href = link.href;
    card.appendChild(linkWrapper);

    const media = document.createElement('div');
    media.classList.add('mega-search__product-media');
    linkWrapper.appendChild(media);

    const sourceImage = link.querySelector('img');
    if (sourceImage) {
      const image = sourceImage.cloneNode(true);
      image.removeAttribute('srcset');
      image.loading = 'lazy';
      image.decoding = 'async';
      image.classList.add('mega-search__product-media-image', 'mega-search__product-image');
      media.appendChild(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.classList.add('mega-search__product-image');
      placeholder.setAttribute('aria-hidden', 'true');
      media.appendChild(placeholder);
    }

    const info = document.createElement('div');
    info.classList.add('mega-search__product-info');
    linkWrapper.appendChild(info);

    const title =
      link.querySelector('.predictive-search__item-heading')?.textContent?.trim() || link.textContent.trim();
    const titleEl = document.createElement('p');
    titleEl.classList.add('mega-search__product-title', 'text-body', 'text-400');
    titleEl.textContent = title;
    info.appendChild(titleEl);

    const priceText =
      link
        .querySelector(
          '.predictive-search__item-price, .predictive-search__item-price-current, .price-item, .price-item--regular',
        )
        ?.textContent?.trim() || '';

    if (priceText) {
      const priceEl = document.createElement('p');
      priceEl.classList.add('mega-search__product-price', 'text-body', 'text-600');
      priceEl.textContent = priceText;
      info.appendChild(priceEl);
    }

    return card;
  }

  updateSuggestions(suggestionItems, headingText) {
    if (!this.megaElements?.suggestionsList) return;

    const { suggestionsContainer, suggestionsList, suggestionsTitle } = this.megaElements;
    const hasDefaultMarkup =
      typeof this.defaultSuggestionsMarkup === 'string' && this.defaultSuggestionsMarkup.trim() !== '';
    const defaultTitle = this.defaultSuggestionsTitle || '';

    if (!suggestionItems.length) {
      if (hasDefaultMarkup) {
        suggestionsList.innerHTML = this.defaultSuggestionsMarkup;
        if (suggestionsTitle) {
          suggestionsTitle.textContent = defaultTitle;
        }
        suggestionsContainer?.removeAttribute('hidden');
      } else {
        suggestionsList.innerHTML = '';
        suggestionsContainer?.setAttribute('hidden', 'true');
      }
      return;
    }

    suggestionsContainer?.removeAttribute('hidden');
    if (suggestionsTitle) {
      suggestionsTitle.textContent = headingText || defaultTitle;
    }

    suggestionsList.innerHTML = '';
    const limitedItems = suggestionItems.slice(0, 4);
    limitedItems.forEach((item) => {
      const link = item.querySelector('a');
      if (!link) return;

      const chip = document.createElement('a');
      chip.classList.add('mega-search__chip', 'text-body', 'text-400');
      chip.href = link.href;

      const iconSpan = document.createElement('span');
      iconSpan.classList.add('mega-search__chip-icon', 'svg-wrapper');
      if (this.suggestionIconHTML) {
        iconSpan.innerHTML = this.suggestionIconHTML;
      }
      chip.appendChild(iconSpan);

      const labelSpan = document.createElement('span');
      labelSpan.classList.add('mega-search__chip-label');
      labelSpan.textContent = link.textContent.trim();
      chip.appendChild(labelSpan);

      suggestionsList.appendChild(chip);
    });
  }

  updateCta(searchForButton) {
    const footer = this.searchDropdown?.querySelector('[data-search-default-footer]');
    if (!footer) return;

    const button = footer.querySelector('[data-search-cta]');
    if (!button) return;

    footer.removeAttribute('hidden');
    footer.hidden = false;
    button.removeAttribute('hidden');
    button.hidden = false;
    button.disabled = false;
    button.removeAttribute('disabled');

    const label = searchForButton?.textContent?.trim() || this.buildSearchLabel(this.input.value.trim());
    const primaryLabel = button.querySelector('.component-button__label');
    const secondaryLabel = button.querySelector('.component-button__label-secondary');

    if (primaryLabel) primaryLabel.textContent = label;
    if (secondaryLabel) secondaryLabel.textContent = label;

    if (this.ctaClickHandler) {
      button.removeEventListener('click', this.ctaClickHandler);
    }

    button.type = 'button';
    button.dataset.searchCta = 'results';

    const searchUrl = this.buildSearchUrl(this.input.value.trim());
    this.ctaClickHandler = () => {
      if (searchUrl) {
        this.closeFlyout();
        window.location.href = searchUrl;
      }
    };
    button.addEventListener('click', this.ctaClickHandler);
  }

  filterProductItems(items, maxResults) {
    if (!items?.length) return [];
    if (!this.shouldFilterByVehicle() || !this.vehicleMatchers) return items.slice(0, maxResults);

    const strictMatches = [];
    const looseVehicleMatches = [];
    const brandOnlyMatches = [];

    for (const item of items) {
      if (this.matchesVehicle(item)) {
        strictMatches.push(item);
        if (strictMatches.length >= maxResults) break;
        continue;
      } else if (this.matchesVehicleLoose(item) && looseVehicleMatches.length < maxResults) {
        looseVehicleMatches.push(item);
        continue;
      }

      if (this.matchesVehicleBrandOnly(item) && brandOnlyMatches.length < maxResults) {
        brandOnlyMatches.push(item);
      }
    }

    if (strictMatches.length) return strictMatches;
    if (looseVehicleMatches.length) return looseVehicleMatches;
    if (brandOnlyMatches.length) return brandOnlyMatches;

    return [];
  }

  matchesVehicle(item) {
    if (!item || !this.vehicleMatchers) return true;
    const { brandTokens = [], modelTokens = [], coreTokens = [] } = this.vehicleMatchers;
    if (!brandTokens.length) return true;

    const productTokens = this.collectProductTokens(item);
    if (!productTokens.length) return false;

    const tokenSet = new Set(productTokens);

    const hasBrand = brandTokens.some((token) => tokenSet.has(token));
    if (!hasBrand) return false;

    if (modelTokens.length > 0) {
      const hasModel = modelTokens.some((token) => tokenSet.has(token));
      return hasModel;
    }

    if (!coreTokens.length) return true;
    const hasCore = coreTokens.some((token) => tokenSet.has(token));
    return hasCore;
  }

  matchesVehicleLoose(item) {
    if (!item || !this.vehicleMatchers) return true;
    const { brandTokens = [], modelTokens = [], coreTokens = [] } = this.vehicleMatchers;
    if (!brandTokens.length) return true;

    const productTokens = this.collectProductTokens(item);
    if (!productTokens.length) return false;
    const tokenSet = new Set(productTokens);

    const hasBrand = brandTokens.some((token) => tokenSet.has(token));
    if (!hasBrand) return false;

    const vehicleTokens = [...modelTokens, ...coreTokens].filter((t) => t && t.length >= 2);
    if (!vehicleTokens.length) return true;

    return vehicleTokens.some((vt) => productTokens.some((pt) => pt.includes(vt) || vt.includes(pt)));
  }

  matchesVehicleBrandOnly(item) {
    if (!item || !this.vehicleMatchers) return true;
    const { brandTokens = [] } = this.vehicleMatchers;
    if (!brandTokens.length) return true;

    const productTokens = this.collectProductTokens(item);
    if (!productTokens.length) return false;

    const tokenSet = new Set(productTokens);
    return brandTokens.some((token) => tokenSet.has(token));
  }

  collectProductTokens(item) {
    const tokens = new Set();
    const anchor = item?.querySelector('a');

    if (anchor) {
      if (anchor.pathname) {
        const handle = anchor.pathname.split('/').pop();
        this.normalizeToTokens(handle).forEach((token) => tokens.add(token));
      }

      this.normalizeToTokens(anchor.textContent).forEach((token) => tokens.add(token));
    }

    this.normalizeToTokens(item?.textContent).forEach((token) => tokens.add(token));

    return Array.from(tokens);
  }

  buildSearchLabel(term) {
    if (!term) return '';
    if (!this.searchLabelTemplate) return '';
    return this.searchLabelTemplate.replace('__TERM__', term);
  }

  buildSearchUrl(term) {
    const action = this.input?.form?.getAttribute('action') || '/search';
    if (!term) return action;
    const params = new URLSearchParams();

    const query = this.shouldFilterByVehicle() ? this.buildVehicleSearchTerm(term) : term;

    params.set('q', query);
    params.set('options[prefix]', 'last');
    if (this.shouldFilterByVehicle()) {
      params.set('vehicle', this.selectedVehicleName);
    }
    return `${action}?${params.toString()}`;
  }

  restoreDefaultMegaSearch(force = false) {
    if (!this.megaElements) return false;

    if (!force && activeMegaSearchOwner && activeMegaSearchOwner !== this) {
      return false;
    }

    if (force && activeMegaSearchOwner === this) {
      activeMegaSearchOwner = null;
    }

    if (this.ctaClickHandler) {
      const activeButton = this.searchDropdown?.querySelector('[data-search-cta]');
      activeButton?.removeEventListener('click', this.ctaClickHandler);
      this.ctaClickHandler = null;
    }

    if (typeof this.defaultProductMarkup !== 'undefined' && this.megaElements.defaultGrid) {
      this.megaElements.defaultGrid.innerHTML = this.defaultProductMarkup;
    }

    if (typeof this.defaultSuggestionsMarkup !== 'undefined' && this.megaElements.suggestionsList) {
      this.megaElements.suggestionsList.innerHTML = this.defaultSuggestionsMarkup;
    }

    if (typeof this.defaultSuggestionsTitle !== 'undefined' && this.megaElements.suggestionsTitle) {
      this.megaElements.suggestionsTitle.textContent = this.defaultSuggestionsTitle;
    }

    if (typeof this.defaultFooterHTML !== 'undefined' && this.megaElements.footer) {
      this.megaElements.footer.innerHTML = this.defaultFooterHTML;
    }

    this.megaElements.suggestionsContainer?.removeAttribute('hidden');
    this.searchDropdown?.classList.remove('header__search-dropdown--has-results');

    this.cacheMegaElements();
    this.storeDefaultMegaState();
    return true;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false, options = {}) {
    if (typeof clearSearchTerm === 'object') {
      options = clearSearchTerm;
      clearSearchTerm = false;
    }

    this.closeResults(clearSearchTerm, options);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false, { preserveMega = false } = {}) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    if (!preserveMega) {
      const forceReset = clearSearchTerm || !this.searchTerm?.trim()?.length;
      const restored = this.restoreDefaultMegaSearch(forceReset);
      if (restored) {
        this.showMegaDefaultView(false);
        this.removeAttribute('results');
      }
    }
  }

  // Search flyout methods
  openFlyout() {
    if (!this.searchDropdown) return;
    this.flyoutJustOpened = true;
    this.searchDropdown.classList.add('is-open');
    this.showOverlay();
    // Reset the flag after a short delay to allow the click event to pass
    setTimeout(() => {
      this.flyoutJustOpened = false;
    }, 100);
  }

  closeFlyout() {
    if (!this.searchDropdown) return;
    this.searchDropdown.classList.remove('is-open');
    this.flyoutJustOpened = false;
    this.hideOverlay();
    if (this.input === document.activeElement) {
      this.input.blur();
    }
  }

  isFlyoutOpen() {
    return this.searchDropdown?.classList.contains('is-open');
  }

  onDocumentClick(event) {
    if (!this.searchDropdown || this.flyoutJustOpened) return;

    // Check if click is outside both the input and the flyout
    const clickedInput = event.target.closest('.header__search, .header__search-field');
    const clickedFlyout = event.target.closest('[data-header-search-dropdown]');

    if (!clickedInput && !clickedFlyout) {
      this.closeFlyout();
    }
  }

  onPointerDown(event) {
    this.pointerDownInsideFlyout = this.searchDropdown?.contains(event.target) || false;
  }

  showOverlay() {
    this.searchOverlay ??= document.querySelector('[data-header-search-overlay]');
    if (!this.searchOverlay) return;
    const headerBottom = document.querySelector('.header')?.getBoundingClientRect().bottom || 0;
    this.searchOverlay.style.top = `${headerBottom}px`;
    this.searchOverlay.classList.add('is-visible');
    this.searchOverlay.setAttribute('aria-hidden', 'false');
  }

  hideOverlay(force = false) {
    this.searchOverlay ??= document.querySelector('[data-header-search-overlay]');
    if (!this.searchOverlay) return;
    const keepVisible = !force && document.querySelector('.header')?.classList.contains('header--search-mode');
    if (keepVisible) return;
    this.searchOverlay.classList.remove('is-visible');
    this.searchOverlay.setAttribute('aria-hidden', 'true');
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onPointerDownHandler);
    document.removeEventListener('click', this.onDocumentClickHandler);
    if (this.megaToggle && this.megaToggle.dataset.vehicleToggleBound) {
      this.megaToggle.removeEventListener('change', this.handleVehicleToggle);
      delete this.megaToggle.dataset.vehicleToggleBound;
    }
    if (activeMegaSearchOwner === this) {
      activeMegaSearchOwner = null;
    }
    if (typeof this.vehicleStateUnsubscribe === 'function') {
      this.vehicleStateUnsubscribe();
      this.vehicleStateUnsubscribe = null;
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
